'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Bookmark, Lightbulb, ArrowRight, Loader2, Heart, DollarSign, Zap } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import StarRating from '@/components/ui/StarRating';
import { createClient } from '@/lib/supabase/client';

interface ProblemRow {
  id: string;
  title: string;
  category: string;
  category_color: string;
  problem_status: 'open' | 'under-review' | 'funded' | 'closed';
  excerpt: string;
  funding_target: number;
  funding_raised: number;
  image_url: string | null;
  image_alt: string | null;
  created_at: string;
  user_profiles: { full_name: string; reputation: number } | null;
}

interface SolutionRow {
  id: string;
  title: string;
  excerpt: string;
  solution_status: 'proposed' | 'negotiating' | 'funded' | 'launched';
  funding_ask: number;
  funding_offered: number;
  rating: number;
  rating_count: number;
  tags: string[];
  image_url: string | null;
  image_alt: string | null;
  created_at: string;
  user_profiles: { full_name: string } | null;
  problems: { title: string } | null;
}

export default function TrendingSection() {
  const [problems, setProblems] = useState<ProblemRow[]>([]);
  const [solutions, setSolutions] = useState<SolutionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [bookmarked, setBookmarked] = useState<Set<string>>(new Set());
  const [liked, setLiked] = useState<Set<string>>(new Set());

  useEffect(() => {
    async function fetchData() {
      const supabase = createClient();
      const problemsResponse = await supabase
        .from('problems')
        .select('id, title, category, category_color, problem_status, excerpt, funding_target, funding_raised, image_url, image_alt, created_at, user_profiles(full_name, reputation)')
        .order('created_at', { ascending: false })
        .limit(4);
      const solutionsResponse = await supabase
        .from('solutions')
        .select('id, title, excerpt, solution_status, funding_ask, funding_offered, rating, rating_count, tags, image_url, image_alt, created_at, user_profiles(full_name), problems(title)')
        .order('funding_offered', { ascending: false })
        .limit(4);
      if (problemsResponse.data) setProblems(problemsResponse.data as ProblemRow[]);
      if (solutionsResponse.data) setSolutions(solutionsResponse.data as SolutionRow[]);
      setLoading(false);
    }
    fetchData();
  }, []);

  function toggleBookmark(id: string) {
    setBookmarked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleLike(id: string) {
    setLiked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  if (loading) {
    return (
      <section className="py-16 lg:py-20">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 flex justify-center">
          <Loader2 size={28} className="animate-spin text-primary" />
        </div>
      </section>
    );
  }

  if (problems.length === 0 && solutions.length === 0) return null;

  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 xl:gap-12">

          {/* ── Trending Problems Column ── */}
          <div className="flex flex-col">
            {/* Column Header */}
            <div className="mb-6">
              <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-1">Most Discussed</p>
              <h2 className="text-2xl font-bold text-foreground">Trending Problems</h2>
              <p className="text-muted-foreground mt-1 text-sm">Real challenges gaining the most attention right now</p>
            </div>

            {/* Problem Cards */}
            <div className="flex flex-col gap-4 flex-1">
              {problems.map((problem) => {
                const fundingPct = problem.funding_target > 0
                  ? Math.round((problem.funding_raised / problem.funding_target) * 100)
                  : 0;
                const posterName = problem.user_profiles?.full_name || 'Anonymous';
                const reputation = problem.user_profiles?.reputation ?? 50;
                return (
                  <div
                    key={problem.id}
                    className="bg-card border border-border rounded-xl overflow-hidden card-shadow hover:card-shadow-md hover:border-primary/30 transition-all duration-200 flex gap-0 group">

                    {/* Thumbnail */}
                    {problem.image_url ? (
                      <div className="w-24 shrink-0 bg-muted overflow-hidden">
                        <img
                          src={problem.image_url}
                          alt={problem.image_alt || problem.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    ) : (
                      <div className="w-24 shrink-0 bg-muted/50 flex items-center justify-center">
                        <Lightbulb size={22} className="text-muted-foreground/40" />
                      </div>
                    )}

                    <div className="p-4 flex flex-col gap-2 flex-1 min-w-0">
                      {/* Top row */}
                      <div className="flex items-start justify-between gap-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${problem.category_color || 'bg-gray-100 text-gray-700'}`}>
                          {problem.category}
                        </span>
                        <button
                          onClick={() => toggleBookmark(problem.id)}
                          className="p-1 rounded-lg hover:bg-muted transition-colors shrink-0"
                          aria-label={bookmarked.has(problem.id) ? 'Remove bookmark' : 'Bookmark problem'}>
                          <Bookmark
                            size={13}
                            className={bookmarked.has(problem.id) ? 'fill-primary text-primary' : 'text-muted-foreground'} />
                        </button>
                      </div>

                      {/* Title */}
                      <Link href={`/problem/${problem.id}`}>
                        <h3 className="text-sm font-bold text-foreground leading-snug line-clamp-2 group-hover:text-primary transition-colors duration-150">
                          {problem.title}
                        </h3>
                      </Link>

                      {/* Funding progress */}
                      {problem.funding_raised > 0 && (
                        <div>
                          <div className="flex justify-between text-xs text-muted-foreground mb-1">
                            <span className="font-medium text-success">${(problem.funding_raised / 1000).toFixed(0)}K raised</span>
                            <span>{fundingPct}%</span>
                          </div>
                          <div className="h-1 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(fundingPct, 100)}%` }} />
                          </div>
                        </div>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between mt-auto">
                        <div className="flex items-center gap-1.5">
                          <div className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="text-[10px] font-bold text-primary">{posterName.charAt(0)}</span>
                          </div>
                          <span className="text-xs text-muted-foreground truncate max-w-[70px]">{posterName}</span>
                          <span className="text-xs font-semibold text-success">⭐ {reputation}</span>
                        </div>
                        <StatusBadge variant={problem.problem_status} size="sm" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* View More */}
            <div className="mt-6">
              <Link
                href="/problem-solution-board"
                className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl border border-primary/30 text-sm font-semibold text-primary hover:bg-primary/5 transition-colors duration-200">
                View More Problems <ArrowRight size={15} />
              </Link>
            </div>
          </div>

          {/* ── Trending Solutions Column ── */}
          <div className="flex flex-col">
            {/* Column Header */}
            <div className="mb-6">
              <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-1">Top Entrepreneur Pitches</p>
              <h2 className="text-2xl font-bold text-foreground">Trending Solutions</h2>
              <p className="text-muted-foreground mt-1 text-sm">Most active solutions attracting investor interest</p>
            </div>

            {/* Solution Cards */}
            <div className="flex flex-col gap-4 flex-1">
              {solutions.map((sol) => {
                const fundingPct = sol.funding_ask > 0
                  ? Math.round((sol.funding_offered / sol.funding_ask) * 100)
                  : 0;
                const entrepreneurName = sol.user_profiles?.full_name || 'Anonymous';
                return (
                  <div
                    key={sol.id}
                    className="bg-card border border-border rounded-xl overflow-hidden card-shadow hover:card-shadow-md hover:border-primary/30 transition-all duration-200 flex gap-0 group">

                    {/* Thumbnail */}
                    {sol.image_url ? (
                      <div className="w-24 shrink-0 bg-muted overflow-hidden">
                        <img
                          src={sol.image_url}
                          alt={sol.image_alt || sol.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    ) : (
                      <div className="w-24 shrink-0 bg-muted/50 flex items-center justify-center">
                        <Zap size={22} className="text-muted-foreground/40" />
                      </div>
                    )}

                    <div className="p-4 flex flex-col gap-2 flex-1 min-w-0">
                      {/* Status + Like */}
                      <div className="flex items-center justify-between">
                        <StatusBadge variant={sol.solution_status} size="sm" />
                        <button
                          onClick={() => toggleLike(sol.id)}
                          className="p-1 rounded-lg hover:bg-muted transition-colors"
                          aria-label="Like solution">
                          <Heart size={13} className={liked.has(sol.id) ? 'fill-danger text-danger' : 'text-muted-foreground'} />
                        </button>
                      </div>

                      {/* Title */}
                      <Link href={`/solution/${sol.id}`}>
                        <h3 className="text-sm font-bold text-foreground leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                          {sol.title}
                        </h3>
                      </Link>

                      {sol.problems?.title && (
                        <p className="text-xs text-muted-foreground line-clamp-1">
                          Solving: <span className="font-medium text-foreground/70">{sol.problems.title}</span>
                        </p>
                      )}

                      {/* Funding */}
                      <div className="flex items-center gap-2 text-xs">
                        <DollarSign size={11} className="text-success shrink-0" />
                        <span className="font-semibold text-foreground">${(sol.funding_offered / 1000).toFixed(0)}K</span>
                        <span className="text-muted-foreground">of ${(sol.funding_ask / 1000).toFixed(0)}K ask</span>
                        <span className="ml-auto text-primary font-semibold">{fundingPct}%</span>
                      </div>
                      <div className="h-1 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${fundingPct >= 100 ? 'bg-success' : 'bg-primary'}`}
                          style={{ width: `${Math.min(fundingPct, 100)}%` }} />
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between mt-auto">
                        <div className="flex items-center gap-1.5">
                          <div className="w-4 h-4 rounded-full bg-violet-100 flex items-center justify-center">
                            <span className="text-[10px] font-bold text-violet-600">{entrepreneurName.charAt(0)}</span>
                          </div>
                          <span className="text-xs text-muted-foreground truncate max-w-[80px]">{entrepreneurName}</span>
                        </div>
                        {sol.rating_count > 0 && (
                          <div className="flex items-center gap-1">
                            <StarRating value={Math.round(sol.rating)} size={11} />
                            <span className="text-xs font-semibold text-foreground">{Number(sol.rating).toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* View More */}
            <div className="mt-6">
              <Link
                href="/problem-solution-board?tab=solutions"
                className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl border border-primary/30 text-sm font-semibold text-primary hover:bg-primary/5 transition-colors duration-200">
                View More Solutions <ArrowRight size={15} />
              </Link>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
